import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// 크로스브라우저 호환성을 위한 글로벌 CSS
import './styles/cross-browser-compatibility.css';

// 정적 import - 필수 컴포넌트 (인증, 레이아웃, 에러 처리)
import AdminRoute from './components/Auth/AdminRoute';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { ErrorBoundary, OfflineIndicator, ThemeErrorBoundary } from './components/Error';
import AdminLayout from './components/Layout/AdminLayout';
import Layout from './components/Layout/Layout';
import { PageLoader } from './components/Loading';
import { OnboardingTour } from './components/Onboarding';
import { HOME_PATH } from './constants/navigation';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomThemeProvider, useThemeMode } from './contexts/ThemeContext';
import Login from './pages/Login';

// Lazy import - 사용자 페이지 (코드 분할)
const Home = lazy(() => import('./pages/Home'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Accounts = lazy(() => import('./pages/Accounts'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Dividends = lazy(() => import('./pages/Dividends'));
const Simulation = lazy(() => import('./pages/Simulation'));
const IPO = lazy(() => import('./pages/IPO'));
const AIAnalysis = lazy(() => import('./pages/AIAnalysis'));
const StrategyInvestment = lazy(() => import('./pages/StrategyInvestment'));
const Settings = lazy(() => import('./pages/Settings'));
const ThemePreview = lazy(() => import('./pages/ThemePreview'));
const StockDetail = lazy(() => import('./pages/StockDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Lazy import - 관리자 페이지 (코드 분할)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const DatabaseStats = lazy(() => import('./pages/admin/DatabaseStats'));
const PerformanceMonitoring = lazy(() => import('./pages/admin/PerformanceMonitoring'));
const RateLimitMonitoring = lazy(() => import('./pages/admin/RateLimitMonitoring'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const TaskLogs = lazy(() => import('./pages/admin/TaskLogs'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));
const PromptManager = lazy(() => import('./pages/admin/PromptManager'));
const NotificationsAdmin = lazy(() => import('./pages/admin/Notifications'));
const BackupAdmin = lazy(() => import('./pages/admin/Backup'));

// 온보딩을 포함한 내부 앱 컴포넌트
function AppContent() {
  const { showOnboarding, closeOnboarding } = useAuth();
  const { theme } = useThemeMode();
  const navigate = useNavigate();


  const handleOnboardingNavigate = (path) => {
    navigate(path);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Material-UI 테마 오류 방지 래퍼 */}
      <ThemeErrorBoundary componentName="Global Styles">
        {/* 글로벌 스타일 - 그라디언트 배경 */}
        <GlobalStyles
          styles={{
            body: {
              background: theme.palette.background.default,
              minHeight: '100vh',
            },
            '#root': {
              minHeight: '100vh',
              background: theme.palette.background.default,
            },
          // 스크롤바 커스터마이징
          '*::-webkit-scrollbar': {
            width: '6px',
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark'
              ? 'rgba(51, 65, 85, 0.3)'
              : 'rgba(226, 232, 240, 0.5)',
            borderRadius: '3px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? 'rgba(100, 116, 139, 0.6)'
              : 'rgba(100, 116, 139, 0.4)',
            borderRadius: '3px',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(100, 116, 139, 0.8)'
                : 'rgba(100, 116, 139, 0.6)',
            },
          },
          // 애니메이션 추가
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes slideIn': {
            from: { opacity: 0, transform: 'translateX(-20px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.5 },
            '100%': { opacity: 1 },
          },
          // 미니멀 효과를 위한 추가 CSS
          '.glass-effect': {
            background: theme.palette.background.paper,
            backdropFilter: 'none',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(51, 65, 85, 0.3)'
              : '1px solid rgba(226, 232, 240, 0.5)',
            borderRadius: '8px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          },
        }}
        />
      </ThemeErrorBoundary>

      {/* 오프라인 상태 표시 */}
      <OfflineIndicator />

      {/* 메인 애플리케이션 */}
      <ErrorBoundary title="애플리케이션 오류">
        <Routes>
          {/* 로그인 페이지 (인증 불필요) */}
          <Route path="/login" element={<Login />} />

          {/* 보호된 라우트들 (인증 필요) */}
          <Route path="/" element={
            <ProtectedRoute>
              <ErrorBoundary title="페이지 로딩 오류" compact>
                <Layout />
              </ErrorBoundary>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to={HOME_PATH} replace />} />
            <Route path="home" element={
              <ErrorBoundary title="홈 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Home />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="portfolio" element={
              <ErrorBoundary title="포트폴리오 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Portfolio />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="accounts" element={
              <ErrorBoundary title="계좌 관리 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Accounts />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="transactions" element={
              <ErrorBoundary title="거래 내역 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Transactions />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="dividends" element={
              <ErrorBoundary title="배당 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Dividends />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="simulation" element={
              <ErrorBoundary title="시뮬레이션 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Simulation />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="strategy-investment" element={
              <ErrorBoundary title="전략투자 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <StrategyInvestment />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="ipo" element={
              <ErrorBoundary title="공모주 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <IPO />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="ai-analysis" element={
              <ErrorBoundary title="AI분석 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <AIAnalysis />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="settings" element={
              <ErrorBoundary title="설정 페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="theme-preview" element={
              <ErrorBoundary title="테마 미리보기 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <ThemePreview />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="stocks/:stockCode" element={
              <ErrorBoundary title="종목 상세 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <StockDetail />
                </Suspense>
              </ErrorBoundary>
            } />
            {/* 없는 주소: 보호된 레이아웃 안에 두어, 비로그인은 먼저 로그인으로 보낸다(주소 존재 여부 비노출) */}
            <Route path="*" element={
              <ErrorBoundary title="페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <NotFound />
                </Suspense>
              </ErrorBoundary>
            } />
          </Route>

          {/* 관리자 전용 라우트 (별도 레이아웃) */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminRoute>
                <ErrorBoundary title="관리자 페이지 오류" compact>
                  <AdminLayout />
                </ErrorBoundary>
              </AdminRoute>
            </ProtectedRoute>
          }>
            <Route index element={
              <ErrorBoundary title="관리자 대시보드 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="database" element={
              <ErrorBoundary title="데이터베이스 통계 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <DatabaseStats />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="performance" element={
              <ErrorBoundary title="성능 모니터링 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <PerformanceMonitoring />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="rate-limit" element={
              <ErrorBoundary title="Rate Limit 모니터링 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <RateLimitMonitoring />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="users" element={
              <ErrorBoundary title="사용자 관리 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <UserManagement />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="task-logs" element={
              <ErrorBoundary title="작업 로그 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <TaskLogs />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="settings" element={
              <ErrorBoundary title="시스템 설정 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <SystemSettings />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="prompts" element={
              <ErrorBoundary title="프롬프트 관리 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <PromptManager />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="notifications" element={
              <ErrorBoundary title="공지 관리 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <NotificationsAdmin />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="backup" element={
              <ErrorBoundary title="백업/복원 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <BackupAdmin />
                </Suspense>
              </ErrorBoundary>
            } />
            {/* 없는 관리자 주소: AdminRoute 안이라 관리자가 아니면 기존 차단이 먼저 동작한다(주소 존재 여부 비노출) */}
            <Route path="*" element={
              <ErrorBoundary title="페이지 오류" compact>
                <Suspense fallback={<PageLoader />}>
                  <NotFound homePath="/admin" homeLabel="관리자 대시보드로 이동" />
                </Suspense>
              </ErrorBoundary>
            } />
          </Route>
        </Routes>

        {/* 온보딩 투어 */}
        <OnboardingTour
          open={showOnboarding}
          onClose={closeOnboarding}
          onNavigate={handleOnboardingNavigate}
        />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <Router basename={process.env.PUBLIC_URL || ''}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </CustomThemeProvider>
  );
}

export default App;
